/**
 * Voice Nav dest-dock "FAB profiles": one tap swaps which pills show by default.
 *
 * A profile only rewrites the existing dest-dock order + hidden lists
 * (`zwn-dest-dock-order-v5` / `zwn-dest-dock-hidden-v1`). Pills outside the
 * profile are *hidden*, not deleted, so they stay restorable from Archived FABs.
 * Archived, Learn and Theme stay visible in every profile, and AR View
 * (`compass`) stays available unless explicitly archived.
 */
import {
  DEFAULT_DEST_DOCK_ORDER,
  DEST_DOCK_BUG_ID,
  DEST_DOCK_CORE_IDS,
  DEST_DOCK_PAINT_ID,
  isDestDockPillId,
  pinDeskPillsFirst,
  promoteCompassForDockViewport,
  sanitizeDestDockHidden,
} from './destDockOrder';

export const FAB_PROFILE_KEY = 'zwn-fab-profile-v1';
export const FIELD_SAFETY_DOCK_REVISION = '2026-10-12';

export const FAB_PROFILE_IDS = ['field-contractor', 'contractor-office', 'operator-office', 'finance', 'contractor', 'safety', 'fleet', 'parts', 'customer'] as const;
export type FabProfileId = (typeof FAB_PROFILE_IDS)[number];

export interface FabProfile {
  id: FabProfileId;
  label: string;
  /** Dest-dock ids, left → right. */
  pills: readonly string[];
}

const FIELD_SAFETY_TOOLS = [
  'forms', 'search', 'favorites', 'map', 'roadHazard', 'post', DEST_DOCK_PAINT_ID,
  'doppler', 'certScan', 'delivery', 'news', 'api-browser', DEST_DOCK_BUG_ID, 'learn', 'archived',
] as const;

/**
 * Field and Safety share tool order after their opening pills.
 * Id mapping: Services = `delivery`, Browser =
 * `api-browser`, 2D Map = `map`, Search = `search` (Facility Chips),
 * Report = `bug`, CB Contractor Book = `contractorBook`,
 * P$ Production Atlas = `productionAtlas`.
 */
export const FAB_PROFILES: readonly FabProfile[] = [
  {
    id: 'field-contractor',
    label: 'Field',
    pills: ['compass', 'fieldDesk', ...FIELD_SAFETY_TOOLS],
  },
  {
    id: 'contractor-office',
    label: 'Office',
    // Bids moved into Contractor Desk → Bids (same Open Bids board), so the standalone
    // Bids pill is hidden here → restorable from Archived FABs. Operator office keeps it.
    // Verify + Background moved into Office Desk (header M3 icons), so they are no longer pills.
    pills: [
      'contractors', 'office', 'financeDesk', 'fieldDesk', 'salesBoard', 'calculations', 'contractorBook', 'company', 'fleetDesk', 'partsDesk', 'delivery',
      'safety', 'news', 'post', 'api-browser', 'search', DEST_DOCK_BUG_ID,
    ],
  },
  {
    id: 'operator-office',
    label: 'Operator',
    // Operator Desk (`operators`) leads; the rest keep their order.
    pills: [
      'operators', 'financeDesk', 'office', 'productionAtlas', 'delivery', 'activeOperations', 'company', 'fleetDesk',
      'doppler', 'favorites', 'search', DEST_DOCK_BUG_ID,
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    pills: ['financeDesk', 'search', DEST_DOCK_BUG_ID],
  },
  {
    id: 'contractor',
    label: 'Contractor',
    pills: [
      'contractors', 'fieldDesk', 'fleetDesk', 'partsDesk', 'company', 'contractorBook',
      'forms', 'safety', 'delivery', 'search', 'map', 'favorites', 'post', DEST_DOCK_BUG_ID,
    ],
  },
  {
    id: 'safety',
    label: 'Safety',
    // Safety leads; the Field desk is omitted. Existing Safety permissions still apply.
    pills: ['safety', 'compass', ...FIELD_SAFETY_TOOLS],
  },
  { id: 'fleet', label: 'Fleet', pills: ['fleetDesk', 'forms', 'map', 'delivery', 'search', DEST_DOCK_BUG_ID] },
  { id: 'parts', label: 'Parts', pills: ['partsDesk', 'contractorBook', 'delivery', 'search', DEST_DOCK_BUG_ID] },
  {
    id: 'customer',
    label: 'Customer',
    // Guest / personal marketplace: Services first, then find / map / favorites / feed.
    pills: [
      'delivery', 'search', 'map', 'favorites', 'news', 'post', 'api-browser', DEST_DOCK_BUG_ID,
    ],
  },
];

/** Chrome pills that every profile keeps (Archived must stay reachable). */
export const FAB_PROFILE_CHROME_IDS: readonly string[] = ['compass', 'ufo', 'learn', 'archived', DEST_DOCK_PAINT_ID];

/** AR is available in every profile; only an explicit archive hides it. */
export function fabProfileShowsArLiveView(_id: FabProfileSelection | null | undefined): boolean {
  return true;
}

export function isFabProfileId(value: unknown): value is FabProfileId {
  return typeof value === 'string' && (FAB_PROFILE_IDS as readonly string[]).includes(value);
}

export function getFabProfile(id: FabProfileId): FabProfile {
  return FAB_PROFILES.find((profile) => profile.id === id) as FabProfile;
}

/**
 * Profile pills that live in the UnifiedFabManager zone layout (rail FABs such as
 * Receipts / Cert Scan / P$ Atlas). These must be pulled out of `layout.archived`
 * when a profile is applied so the dest dock can show them.
 */
export function fabProfileLayoutFabIds(id: FabProfileId): string[] {
  return [...new Set([...getFabProfile(id).pills, ...FAB_PROFILE_CHROME_IDS])].filter((pill) => !isDestDockPillId(pill));
}

export function readFabProfile(): FabProfileId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(FAB_PROFILE_KEY);
    return isFabProfileId(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeFabProfile(id: FabProfileId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAB_PROFILE_KEY, id);
  } catch {
    // Quota / private mode — profile still applies for this session.
  }
}

/** Order + hidden lists for an explicit pill list (shared by profiles and All profiles). */
function buildDockStateForPills(
  pills: readonly string[],
  candidates: readonly string[],
): { order: string[]; hidden: string[] } {
  const order = Array.from(new Set([...pills, ...FAB_PROFILE_CHROME_IDS]));
  const visible = new Set(order);
  const universe = new Set<string>([
    ...DEFAULT_DEST_DOCK_ORDER,
    ...DEST_DOCK_CORE_IDS,
    ...FAB_PROFILES.flatMap((item) => item.pills),
    ...candidates,
  ]);
  const hidden = sanitizeDestDockHidden(Array.from(universe).filter((pill) => !visible.has(pill)));
  return { order, hidden };
}

/**
 * Dest-dock order + hidden lists for a profile.
 * `candidates` = every id that could appear in the dock right now (available pills,
 * rail FABs, overflow tools). Anything not in the profile or chrome is hidden.
 */
export function buildFabProfileDockState(
  id: FabProfileId,
  candidates: readonly string[] = [],
): { order: string[]; hidden: string[] } {
  return buildDockStateForPills(getFabProfile(id).pills, candidates);
}

// ---------------------------------------------------------------------------
// All profiles (staff only): union of every profile's pills.
// ---------------------------------------------------------------------------

/**
 * Header mini-fab "All profiles" option. Staff-only UI chrome — not a role and
 * not AuthZ. Stored in its own key so the user's real profile (FAB_PROFILE_KEY)
 * stays intact underneath and is the fallback when the gate says no.
 */
export const FAB_ALL_PROFILES_ID = 'all-profiles' as const;
export type FabProfileSelection = FabProfileId | typeof FAB_ALL_PROFILES_ID;
export const FAB_ALL_PROFILES_KEY = 'zwn-fab-profile-all-v1';
export const FAB_ALL_PROFILES_LABEL = 'All';

/** Cipher: only these two signed-in emails (exact, case-insensitive) see All profiles. */
export const FAB_ALL_PROFILES_EMAILS: readonly string[] = ['sales@ziawellnav.com', 'support@ziawellnav.com'];

export function isFabAllProfilesEmail(email: string | null | undefined): boolean {
  const normalized = String(email || '').trim().toLowerCase();
  return Boolean(normalized) && FAB_ALL_PROFILES_EMAILS.includes(normalized);
}

/**
 * allowed = signed in as sales@ / support@ziawellnav.com (not demo).
 * denied  = signed in as anyone else (or demo) — a stale All selection is cleared.
 * unknown = signed out / session email not loaded yet — never shows All, but the
 *           stored selection is kept so a slow native session restore does not wipe it.
 */
export type FabAllProfilesGate = 'allowed' | 'denied' | 'unknown';

export function fabAllProfilesGate(input: {
  signedIn: boolean;
  email?: string | null;
  demo?: boolean;
}): FabAllProfilesGate {
  if (input.demo) return 'denied';
  if (!input.signedIn) return 'unknown';
  const email = String(input.email || '').trim();
  if (!email) return 'unknown';
  return isFabAllProfilesEmail(email) ? 'allowed' : 'denied';
}

/**
 * Union of every profile's pills: desk pills first (Operator → Contractor → Office → Field),
 * then left → right grouped by profile (Field → Office → Operator → Customer),
 * de-duplicated, Report last. Learn / Archived / Theme chrome is appended by the dock-state builder.
 */
export const FAB_ALL_PROFILES_PILLS: readonly string[] = (() => {
  const union = Array.from(new Set(FAB_PROFILES.flatMap((profile) => profile.pills)))
    .filter((pill) => pill !== DEST_DOCK_BUG_ID && !FAB_PROFILE_CHROME_IDS.includes(pill));
  return [...pinDeskPillsFirst(union), DEST_DOCK_BUG_ID];
})();

export function fabAllProfilesLayoutFabIds(): string[] {
  return [...FAB_ALL_PROFILES_PILLS, ...FAB_PROFILE_CHROME_IDS].filter((pill) => !isDestDockPillId(pill));
}

export function buildFabAllProfilesDockState(
  candidates: readonly string[] = [],
): { order: string[]; hidden: string[] } {
  return buildDockStateForPills(FAB_ALL_PROFILES_PILLS, candidates);
}

export function readFabAllProfiles(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FAB_ALL_PROFILES_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeFabAllProfiles(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (on) window.localStorage.setItem(FAB_ALL_PROFILES_KEY, '1');
    else window.localStorage.removeItem(FAB_ALL_PROFILES_KEY);
  } catch {
    // Quota / private mode — selection still applies for this session.
  }
}

/** Real profile to fall back to: last stored profile, else account role, else Customer. */
export function fabProfileFallbackId(
  stored: FabProfileId | null | undefined,
  role?: 'operator' | 'contractor' | 'customer' | null,
): FabProfileId {
  if (stored && isFabProfileId(stored)) return stored;
  if (role === 'operator') return 'operator-office';
  if (role === 'contractor') return 'contractor';
  return 'customer';
}

/**
 * What the dock should show: All profiles only when the stored selection is All
 * AND the gate allows it; otherwise the user's normal profile (or null = untouched dock).
 */
export function resolveFabProfileSelection(input: {
  allSelected: boolean;
  gate: FabAllProfilesGate;
  stored: FabProfileId | null | undefined;
  role?: 'operator' | 'contractor' | 'customer' | null;
}): FabProfileSelection | null {
  if (input.allSelected) {
    return input.gate === 'allowed' ? FAB_ALL_PROFILES_ID : fabProfileFallbackId(input.stored, input.role);
  }
  return input.stored && isFabProfileId(input.stored) ? input.stored : null;
}

export function fabProfileAccent(id: FabProfileSelection | null, darkMode = true): string {
  const colors: Record<FabProfileSelection, [string, string]> = {
    'field-contractor': ['#f97316', '#c2410c'],
    'contractor-office': ['#38bdf8', '#0369a1'],
    'operator-office': ['#22c55e', '#15803d'],
    finance: ['#34d399', '#047857'],
    contractor: ['#f97316', '#c2410c'],
    safety: ['#5eead4', '#0f766e'],
    fleet: ['#fbbf24', '#92400e'],
    parts: ['#2dd4bf', '#0f766e'],
    customer: ['#a855f7', '#7e22ce'],
    'all-profiles': ['#facc15', '#854d0e'],
  };
  return id ? colors[id][darkMode ? 0 : 1] : (darkMode ? '#94a3b8' : '#475569');
}

/** Field and Safety keep their exact profile order, including AR, across reloads. */
export function fabProfileDockMergeOptions(profile: FabProfileSelection | null, userArranged = false): { desksFirst: boolean; preserveCompassPosition: boolean } {
  const fieldOrSafety = profile === 'field-contractor' || profile === 'safety';
  return { desksFirst: !fieldOrSafety && !userArranged, preserveCompassPosition: fieldOrSafety };
}

/** Safety leads its row; other profiles retain their AR-first viewport. */
export function fabProfileViewportOrder(ids: readonly string[], profile: FabProfileSelection | null): string[] {
  return profile === 'safety' ? [...ids] : promoteCompassForDockViewport([...ids]);
}
